import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Keyboard, Zoom, EffectFade } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/keyboard';
import 'swiper/css/zoom';
import 'swiper/css/effect-fade';
import { getFileType, getProcessedPhotoUrl } from './utils/photoHelpers';
import { useVideoManager } from './hooks/useVideoManager';
import VideoPlayer from './VideoPlayer';
import DownloadProgressOverlay from './DownloadProgressOverlay';
import { useSingleFileDownload } from './hooks/useSingleFileDownload';

/**
 * PhotoModal component handles the photo viewing modal with Swiper carousel
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Array} props.photos - Array of photo filenames
 * @param {number} props.currentIndex - Current photo index
 * @param {function} props.onClose - Handler for closing the modal
 * @param {function} props.onSlideChange - Handler for slide changes
 * @param {boolean} props.hasNextPage - Whether there are more photos to load
 * @param {function} props.fetchNextPage - Function to fetch next page
 * @param {boolean} props.autoPlay - Whether to auto-play videos
 * @param {number} props.totalPhotos - Total number of photos available
 * @returns {JSX.Element|null}
 */
const PhotoModal = ({
  isOpen,
  photos,
  currentIndex,
  onClose,
  onSlideChange,
  hasNextPage,
  fetchNextPage,
  autoPlay = false,
  totalPhotos = 0
}) => {
  const modalRef = useRef(null);
  const swiperRef = useRef(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(currentIndex);

  // Create photoAccess object for compatibility
  const photoAccess = useMemo(() => {
    if (!photos) return null;
    return {
      getAllLoadedPhotos: () => photos,
      getPhotoByIndex: (index) => photos[index],
      totalPhotos: totalPhotos || photos.length // Use totalPhotos if available, fallback to loaded photos count
    };
  }, [photos, totalPhotos]);

  // Get video files for the video manager - only nearby videos for lazy loading
  const videoFiles = useMemo(() => {
    if (!photoAccess) return [];

    const allPhotos = photoAccess.getAllLoadedPhotos();
    const totalPhotos = allPhotos.length;

    // Only manage videos that are near the active slide index (active ± 2) for lazy loading
    const startIndex = Math.max(0, activeSlideIndex - 2);
    const endIndex = Math.min(totalPhotos - 1, activeSlideIndex + 2);

    const nearbyVideos = [];
    for (let i = startIndex; i <= endIndex; i++) {
      const filename = allPhotos[i];
      if (getFileType(filename) === 'video') {
        nearbyVideos.push(filename);
      }
    }

    return nearbyVideos;
  }, [photoAccess, activeSlideIndex]);

  // Update active slide index when currentIndex prop changes
  useEffect(() => {
    setActiveSlideIndex(currentIndex);
  }, [currentIndex]);

  // Use video manager hook
  const { setVideoPlayers, getStreamData, isStreamLoading, cleanupPlayers } = useVideoManager(videoFiles, isOpen);

  // Use single file download hook
  const { downloadFile, downloadState } = useSingleFileDownload();

  // Swiper event handlers
  const handleSwiperSlideChange = (swiper) => {
    const newIndex = swiper.activeIndex;
    setActiveSlideIndex(newIndex);
    onSlideChange(newIndex);

    // Stop all videos when changing slides to prevent audio bleed
    const allVideos = document.querySelectorAll('video[data-filename]');
    allVideos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    // Auto-play video when navigating to video slide
    const currentFilename = photoAccess?.getPhotoByIndex(newIndex);
    if (currentFilename && getFileType(currentFilename) === 'video') {
      // Play video after slide transition completes
      setTimeout(() => {
        const videoElement = document.querySelector(`video[data-filename="${currentFilename}"]`);
        if (videoElement && swiper.activeIndex === newIndex) {
          // Play from beginning
          videoElement.currentTime = 0;
          // Use play() promise to handle autoplay restrictions
          const playPromise = videoElement.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Video autoplay failed in PhotoModal navigation:', error);
              // User interaction required - video will need manual play
            });
          }
        }
      }, 800); // Increased timeout for video element initialization
    }

    // Load more photos if we're near the end of loaded photos
    if (photoAccess) {
      const loadedPhotosCount = photoAccess.getAllLoadedPhotos().length;
      if (newIndex >= loadedPhotosCount - 5 && hasNextPage) {
        fetchNextPage();
      }
    }
  };

  const handleSwiperInit = (swiper) => {
    swiperRef.current = swiper;
    // Navigate to the correct slide when modal opens
    swiper.slideTo(currentIndex, 0);

    // Auto-play if initial slide is a video - increased timeout for video to load
    const initialFilename = photoAccess?.getPhotoByIndex(currentIndex);
    if (initialFilename && getFileType(initialFilename) === 'video') {
      setTimeout(() => {
        const videoElement = document.querySelector(`video[data-filename="${initialFilename}"]`);
        if (videoElement) {
          videoElement.currentTime = 0;
          // Use play() promise to handle autoplay restrictions
          const playPromise = videoElement.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Video autoplay failed on PhotoModal open:', error);
              // User interaction required - video will need manual play
            });
          }
        }
      }, 800); // Increased timeout to allow video element to fully initialize
    }
  };

  // Helper function to render photo/video content with lazy loading
  const renderPhotoContent = (index) => {
    if (!photoAccess) return null;
    const filename = photoAccess.getPhotoByIndex(index);
    const fileType = getFileType(filename);

    // Implement lazy loading: only load content for nearby slides
    const isNearCurrentSlide = Math.abs(index - activeSlideIndex) <= 1;

    if (fileType === 'video') {
      const streamData = getStreamData(filename);
      const isLoading = isStreamLoading(filename);

      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <div className="video-container w-full max-w-6xl mx-auto">
            <VideoPlayer
              filename={filename}
              streamData={streamData}
              isLoading={isLoading}
              showControls={true}
              autoPlay={false} // Always false - manual play handled by handleSwiperInit/handleSwiperSlideChange
              muted={false}
              preload={isNearCurrentSlide ? "metadata" : "none"} // Only preload metadata for nearby videos
              className="w-full max-h-[80vh] rounded shadow-lg"
              style={{ objectFit: 'contain', maxWidth: '100%', height: 'auto' }}
              setVideoPlayers={setVideoPlayers}
            />
          </div>
        </div>
      );
    } else {
      const photoUrl = getProcessedPhotoUrl(filename);

      if (!isNearCurrentSlide) {
        // For distant slides, show a placeholder
        return (
          <div className="relative w-full h-full flex items-center justify-center">
            <div className="w-full h-auto max-w-4xl max-h-[80vh] rounded shadow-lg bg-gray-200 flex items-center justify-center">
              <div className="text-gray-500 text-lg">Loading...</div>
            </div>
          </div>
        );
      }

      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <img
            src={photoUrl}
            loading="lazy"
            className="w-full h-auto max-w-4xl rounded shadow-lg"
            style={{ maxHeight: '80vh', objectFit: 'contain' }}
            alt={`${index + 1} of ${photoAccess?.totalPhotos || 0}`}
            onError={(e) => {
              // Fallback to app logo if image fails to load (with cache-busting to prevent caching)
              e.target.src = `/UserData/Logo.svg?t=${Date.now()}`;
              e.target.className = 'w-full h-auto max-w-4xl rounded shadow-lg bg-primary-10';
            }}
          />
        </div>
      );
    }
  };

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);



  // Cleanup Plyr players on unmount
  useEffect(() => {
    return () => {
      cleanupPlayers();
    };
  }, [cleanupPlayers]);

  // Handle download with proper cross-browser support using presigned URLs
  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const filename = photoAccess.getPhotoByIndex(currentIndex);
    
    try {
      const fileType = getFileType(filename);
      
      // For videos, use existing stream URL if available (already presigned)
      if (fileType === 'video') {
        const streamData = getStreamData(filename);
        if (streamData?.url) {
          await downloadFile(filename, async () => streamData.url);
          return;
        }
      }
      
      // For other files, use API to get download URL
      await downloadFile(filename);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file. Please try again.');
    }
  };

  if (!isOpen || !photoAccess) return null;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 photo-modal"
      onClick={onClose}
    >
      <div className="relative w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
        {/* Swiper Carousel */}
        <Swiper
          onSwiper={handleSwiperInit}
          onSlideChange={handleSwiperSlideChange}
          initialSlide={currentIndex}
          modules={[Navigation, Keyboard, Zoom, EffectFade]}
          navigation={{
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          }}
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          zoom={{
            maxRatio: 3,
            minRatio: 0.5,
          }}
          spaceBetween={0}
          slidesPerView={1}
          centeredSlides={true}
          loop={false}
          speed={400}
          allowTouchMove={true}
          resistance={true}
          resistanceRatio={0.85}
          effect="fade"
          fadeEffect={{
            crossFade: true
          }}
          className="w-full h-full"
        >
          {Array.from({ length: photos?.length || 0 }, (_, index) => (
            <SwiperSlide key={`photo-${index}`}>
              <div className="swiper-zoom-container w-full h-full flex items-center justify-center">
                {renderPhotoContent(index)}
              </div>
            </SwiperSlide>
          ))}

          {/* Navigation buttons */}
          <div className="swiper-button-prev !text-white !opacity-70 hover:!opacity-100 transition-opacity"></div>
          <div className="swiper-button-next !text-white !opacity-70 hover:!opacity-100 transition-opacity"></div>
        </Swiper>

        {/* Modal header with floating icon buttons */}
        <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-6 py-4 z-10 pointer-events-none">
          <div className="flex items-center space-x-3">
            {photoAccess && (
              <button
                onClick={handleDownload}
                title="Download original file"
                className="pointer-events-auto group"
                aria-label="Download photo"
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white-20 backdrop-blur-md border border-white-20 shadow-lg transition-all duration-200 group-hover:bg-white-30 group-hover:scale-105">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
                  </svg>
                </span>
              </button>
            )}
          </div>

          <button
            onClick={onClose}
            aria-label="Close photo viewer"
            className="pointer-events-auto group"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white-20 backdrop-blur-md border border-white-20 shadow-lg transition-all duration-200 group-hover:bg-white-30 group-hover:scale-105">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          </button>
        </div>

        {/* Photo counter */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10 pointer-events-none">
          <div className="px-6 py-3 rounded-full bg-black-60 backdrop-blur-md text-white text-sm font-medium border border-white-20 shadow-lg">
            <span className="font-semibold">{currentIndex + 1}</span>
            <span className="text-white-70 mx-1">of</span>
            <span>{photoAccess?.totalPhotos || 0}</span>
          </div>
        </div>
      </div>

      {/* Download Progress Overlay */}
      <DownloadProgressOverlay downloadState={downloadState} />
    </div>
  );
};

export default PhotoModal;
